"use strict";

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = RotationGroup;
var _react = _interopRequireWildcard(require("react"));
var _reactKonva = require("react-konva");
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != _typeof(e) && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function degToRad(angle) {
  return angle / 180 * Math.PI;
}
function getRelativeRotation(t0, tf) {
  /**
   * return the offset rotation to move from initial to final angle
   * find minimum offset on unit circle
   */

  return (360 + (tf - t0)) % 360;
}
var getShapeCenter = function getShapeCenter(shape) {
  var x = shape.x,
    y = shape.y,
    width = shape.width,
    height = shape.height;
  return {
    x: x + width / 2,
    y: y + height / 2
  };
};
function rotatePoint(point, deg) {
  var rad = degToRad(deg || 0);
  var rcos = Math.cos(rad);
  var rsin = Math.sin(rad);
  return {
    x: Math.round(point.x * rcos - point.y * rsin),
    y: Math.round(point.x * rsin + point.y * rcos)
  };
}
function rotateAroundPoint(point, origin, rotateBy) {
  var dxdy = {
    x: point.x - origin.x,
    y: point.y - origin.y
  };

  // rotation-in-place
  var rotated = rotatePoint(dxdy, rotateBy);

  // followed by a translation (or move)
  return {
    x: origin.x + rotated.x,
    y: origin.y + rotated.y
  };
}
function scale(shape, scaleTo, origin) {
  var _origin;
  origin = (_origin = origin) !== null && _origin !== void 0 ? _origin : {
    x: shape.x,
    y: shape.y
  };
  var scaleInitial = shape.scale;
  var scaleDelta = {
    x: scaleTo.x / scaleInitial.x,
    y: scaleTo.y / scaleInitial.y
  };
  var position = {
    x: shape.x - (origin.x - shape.x) * (scaleDelta.x - 1),
    y: shape.y - (origin.y - shape.y) * (scaleDelta.y - 1)
  };
  return position;
}

/**
 * Scales the given group to fit the stage, while keeping it centered.
 * @param {Konva.Group} group - The group to scale.
 * @param {boolean} center - Whether to keep the group centered after scaling.
 * @returns {Object} - The new position and scale of the group.
 */
function scaleToFit(group, center) {
  // Compute group dimensions and/or override if not provided
  var boundingBox = group.getClientRect();
  var shape = {
    x: group.x(),
    y: group.y(),
    width: group.width() || boundingBox.width,
    height: group.height() || boundingBox.height,
    scale: group.scale(),
    rotation: group.rotation()
  };
  var position = {
    x: shape.x,
    y: shape.y
  };
  var scaleTo = {
    x: 1,
    y: 1
  };
  if (shape.width > 0 && shape.height > 0) {
    // Get stage properties
    var stage = group.getStage();
    var stageWidth = stage.width();
    var stageHeight = stage.height();

    // Compute the equivalent rotated stage to determine best-fit rescale
    var rad = degToRad(shape.rotation);
    var rcos = Math.cos(rad);
    var rsin = Math.sin(rad);
    var rotated = {
      width: Math.abs(stageWidth * rcos) + Math.abs(stageHeight * rsin),
      height: Math.abs(stageWidth * rsin) + Math.abs(stageHeight * rcos)
    };

    // Get best fit
    var scaleToBestFit = Math.min(stageWidth / rotated.width, stageHeight / rotated.height);

    // compute new position from scale about center point
    scaleTo = {
      x: scaleToBestFit,
      y: scaleToBestFit
    };
    position = scale(shape, scaleTo, center);
  }
  return {
    position: position,
    scale: scaleTo
  };
}
function RotationGroup(_ref) {
  var children = _ref.children,
    rotation = _ref.rotation,
    center = _ref.center;
  var groupRef = (0, _react.useRef)(null);
  var prevRotation = (0, _react.useRef)(0);
  var centerRef = (0, _react.useRef)(null);
  var getCenter = function getCenter() {
    var _centerRef$current;
    // returns the current available center
    var group = groupRef.current;
    var boundary = group.getClientRect();
    return (_centerRef$current = centerRef.current) !== null && _centerRef$current !== void 0 ? _centerRef$current : getShapeCenter(boundary);
  };
  (0, _react.useEffect)(function () {
    // compute rotation about a point
    var group = groupRef.current;
    var moveBy = getRelativeRotation(prevRotation.current, rotation);
    var upperLeft = group.position();
    var point = getCenter();
    var position = rotateAroundPoint(upperLeft, point, moveBy);
    prevRotation.current = rotation;
    group.setAttrs({
      position: position,
      rotation: rotation
    });
    var scaledPosition = scaleToFit(group, point);
    group.setAttrs({
      position: scaledPosition.position,
      scale: scaledPosition.scale
    });

    // update canvas group
    group.getLayer().batchDraw();
  }, [rotation]);
  (0, _react.useEffect)(function () {
    // update local center ref in which group rotates around
    centerRef.current = center;
  }, [center]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactKonva.Group, {
    ref: groupRef,
    children: children
  });
}